// @flow
import { isString } from "voca";
import cssColorNames from "css-color-names";
import { rgb as getContrast, luminance as getLuminance } from "wcag-contrast";
import getRgbStr from "rgb";
import tinycolor from "tinycolor2";

const BLACK = [0, 0, 0, 1];
const WHITE = [255, 255, 255, 1];
const RATINGS = Object.freeze({
	AA: "AA",
	AAA: "AAA",
	F: "F",
});
const { round } = Math;
const { max } = Math;

const getColorRgb = (color: string, asArray = true) => {
	color = cssColorNames[color] || color; // get the value for named color
	return getRgbStr(color, asArray);
};

const calculateContrastRating = (contrast: number, largeText: boolean) => {
	let rating = RATINGS.F;

	if (contrast >= 7) {
		rating = RATINGS.AAA;
	} else if (contrast >= 4.5) {
		rating = largeText ? RATINGS.AAA : RATINGS.AA;
	} else if (largeText && contrast >= 2.6) { // CHEATING !!!!! OFFICIAL VALUE SHOULD BE 3 !!!!!!!!!!! @BOAZ !!!!
		rating = RATINGS.AA;
	}

	return rating;
};

const isRgbOpaque = (rgb: number[]) => rgb.length < 4 || rgb[3] >= 1;

const overlayOn = (overlaidRgb: number[], rgbB: number[]) => {
	const newColor = overlaidRgb.slice();

	if (!isRgbOpaque(newColor)) {
		const alpha = newColor[3];

		for (let i = 0; i < 3; i++) {
			newColor[i] = newColor[i] * alpha + rgbB[i] * rgbB[3] * (1 - alpha);
		}

		newColor[3] = alpha + rgbB[3] * (1 - alpha);
	}

	return newColor;
};

/**
 * based on http://leaverou.github.io/contrast-ratio
 * Ignores the fact that the more the transparent the bg is the more room for error there is
 * because a color underneath it can influence the visual outcome
 *
 * @param rgbA - foreground (text) color (ignores alpha channel)
 * @param rgbB - background color (can have alpha channel)
 * @returns string AA/AAA/F
 */
const calculateContrastForTransparent = (rgbA: number[], rgbB: number[]) => {
	const bgOnBlack = overlayOn(rgbB, BLACK);
	const bgOnWhite = overlayOn(rgbB, WHITE);
	const blackContrast = getContrast(rgbA, bgOnBlack);
	const whiteContrast = getContrast(rgbA, bgOnWhite);
	const ratioMax = max(blackContrast, whiteContrast);
	const lumiA = getLuminance(rgbA);

	const ratioMin = getLuminance(bgOnBlack) > lumiA ? blackContrast
		: getLuminance(bgOnWhite) < lumiA ? whiteContrast : 1;

	return round((ratioMin + ratioMax) / 2);
};

/**
 * from: https://webaim.org/resources/contrastchecker/
 * WCAG 2.0 level AA requires a contrast ratio of at least 4.5:1 for normal text and 3:1 for large text.
 * Level AAA requires a contrast ratio of at least 7:1 for normal text and 4.5:1 for large text.
 * Large text is defined as 14 point (typically 18.66px) and bold or larger, or 18 point (typically 24px) or larger.
 * @param colorA - foreground (text) color (ignores alpha channel)
 * @param colorB - background color (can have alpha channel)
 * @param largeText - (default = false)
 * @returns {string} AA/AAA/F
 */
const getColorsContrastRating = (colorA: string, colorB: string, largeText: boolean = false) => {
	if (!isString(colorA) || !isString(colorB)) {
		throw new Error("getContrastRating - color values must be strings");
	}

	const rgbA = getColorRgb(colorA); // triplet array
	const rgbB = getColorRgb(colorB); // triplet array

	let contrast;

	if (!isRgbOpaque(rgbB)) {
		contrast = calculateContrastForTransparent(rgbA, rgbB);
	} else {
		contrast = getContrast(rgbA, rgbB);
	}

	return calculateContrastRating(contrast, largeText);
};

/**
 * uses getColorsContrastRating and returns true if result is A/AA and false if F
 * @param colorA - foreground (text) color (ignores alpha channel)
 * @param colorB - background color (can have alpha channel)
 * @param largeText - (default = false)
 * @returns {boolean}
 */
const shouldUseColors = (colorA: string, colorB: string, largeText: boolean = false) =>
	getColorsContrastRating(colorA, colorB, largeText) !== RATINGS.F;

const isValidColor = (color: string) => {
	let valid = false;

	try {
		valid = !!getColorRgb(color);
	} finally {
		// fail silently
	}

	return valid;
};

const getTextColorByBackgroundColor = (bgColor: string, lightColor: string, darkColor: string) =>
	(shouldUseColors(lightColor, bgColor, true) ? lightColor : darkColor);

export {
	RATINGS,
	getColorsContrastRating,
	shouldUseColors,
	isValidColor,
	getTextColorByBackgroundColor,
};

/**
 * Changes the hue of the color with opacity
 * @param color
 * @param opacity number between 0-1 or 0 - 100
 * @returns {string} hsl()
 */

export const adjustHue = (color: string, opacity: number = 0.1): string => {
	const bg = tinycolor(color);
	const hsl = bg.toHsl();
	hsl.l = hsl.l > 0.5 ? hsl.l - opacity : hsl.l + opacity;

	return tinycolor(hsl).toHslString();
};
